import { useEffect, useState } from "react";
import { HelpCircle, Mail, MessageCircle, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useContactChannel } from "@/hooks/useContactChannel";
import { buildWhatsappLink } from "@/utils/contactChannel";

const HELP_TOOLTIP_TEXT = "¿No encontraste lo que buscabas? Hacé click acá";

interface AsistenteAyudaButtonProps {
  onOpen: () => void;
}

/** Ícono "(?)" que escala a contacto directo, ubicado debajo del input de `PsiChatWidget`. */
export function AsistenteAyudaButton({ onOpen }: AsistenteAyudaButtonProps) {
  return (
    <div className="flex justify-center pb-2 bg-white">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onOpen}
            aria-label={HELP_TOOLTIP_TEXT}
            className="w-5 h-5 rounded-full border border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 flex items-center justify-center transition-colors"
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{HELP_TOOLTIP_TEXT}</TooltipContent>
      </Tooltip>
    </div>
  );
}

type Step = "confirm" | "channels";

interface AsistenteContactoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal de escalamiento a contacto directo del asistente, en dos pasos: primero confirma que ya
 * se intentó resolver la duda con el asistente/búsqueda (la mayoría de los casos se resuelven
 * así); solo después de esa confirmación muestra los canales (WhatsApp/mail), cada uno detrás de
 * un botón — nunca como texto plano copiable de entrada.
 */
export function AsistenteContactoModal({ open, onOpenChange }: AsistenteContactoModalProps) {
  const [step, setStep] = useState<Step>("confirm");
  // NZ, u O que viene de NZ: único canal es administración/NZ, nunca el WhatsApp/mail de CX-F.
  const { email, whatsappNumber } = useContactChannel();

  useEffect(() => {
    if (open) setStep("confirm");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md w-full p-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 relative" style={{ background: "linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%)" }}>
          <DialogClose
            className="absolute top-4 right-4 rounded-full w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={16} />
          </DialogClose>
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold" style={{ color: "#1e1b4b" }}>
              {step === "confirm" ? "¿Necesitás ayuda?" : "Contacto directo"}
            </DialogTitle>
          </DialogHeader>
        </div>

        {step === "confirm" ? (
          <div className="px-6 pb-6 flex flex-col gap-4">
            <p className="text-sm text-gray-500 leading-snug text-justify">
              La gran mayoría de las dudas se resuelven usando el asistente o buscando en Psi Platform. Te recomendamos intentarlo antes de contactar al equipo directamente.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full rounded-xl border border-purple-200 text-purple-700 font-medium text-sm py-2.5 hover:bg-purple-50 transition-colors"
              >
                Volver a intentar
              </button>
              <button
                type="button"
                onClick={() => setStep("channels")}
                className="w-full rounded-xl bg-purple-600 text-white font-medium text-sm py-2.5 hover:bg-purple-700 transition-colors"
              >
                Ya busqué y no encontré respuesta
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 pb-6 flex flex-col gap-3">
            <p className="text-sm text-gray-500 leading-snug text-justify">
              Comunicate directamente con el equipo por estos medios:
            </p>
            <a
              href={buildWhatsappLink(whatsappNumber)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-2xl border border-green-100 bg-green-50/50 px-5 py-3 text-sm font-semibold text-green-700 hover:bg-green-50 transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              Escribir por WhatsApp
            </a>
            <a
              href={`mailto:${email}`}
              className="flex items-center justify-center gap-2 rounded-2xl border border-purple-100 bg-purple-50/50 px-5 py-3 text-sm font-semibold text-[#6D57BC] hover:bg-purple-50 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Enviar mail
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
